/**
 * English catalog — a full translation of the externalised tranche.
 *
 * Declared `: TranslationKeys`, so TypeScript enforces exact structural parity
 * with the French source (`fr.ts`): a missing key or an unknown extra key fails
 * the build. Any string not yet externalised elsewhere in the app still falls
 * back to French at runtime (see FALLBACK_LOCALE / useT).
 */

import adoptionEn from './adoption.en';
import founderEn from './founder/en';
import planLimitEn from './planLimit/en';
import multiProductEn from './multiProduct/en';
import catchProductsEn from './catchProducts/en';
import trackingDomainsEn from './trackingDomains/en';
import blogDestinationsEn from './blogDestinations/en';
import miIntegrationsEn from './miIntegrations/en';
import type { TranslationKeys } from './fr';
import dataLoopEn from './dataLoop/en';
import autopilotEn from './autopilot/en';
import crmEmailDrafts from './crmEmailDrafts/en';
import emailPurposeTemplates from './emailPurposeTemplates/en';
import crmEmailBulk from './crmEmailBulk/en';
import communityAcquisition from './communityAcquisition/en';
import connectorAccounts from './connectorAccounts/en';
import { i5En } from './i5/en';
import { routesEn } from './routes/en';
import { shellEn } from './shell/en';

const en: TranslationKeys = {
  adoption: adoptionEn,
  founder: founderEn,
  planLimit: planLimitEn,
  multiProduct: multiProductEn,
  catchProducts: catchProductsEn,
  trackingDomains: trackingDomainsEn,
  blogDestinations: blogDestinationsEn,
  miIntegrations: miIntegrationsEn,
  common: {
    language: 'Language',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    create: 'Create',
    edit: 'Edit',
    search: 'Search',
    loading: 'Loading…',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    add: 'Add',
    remove: 'Remove',
    apply: 'Apply',
    reset: 'Reset',
    refresh: 'Refresh',
    retry: 'Retry',
    duplicate: 'Duplicate',
    export: 'Export',
    import: 'Import',
    copy: 'Copy',
    download: 'Download',
    upload: 'Upload',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    actions: 'Actions',
    more: 'More',
    settings: 'Settings',
    processing: 'Processing…',
    readOnly: 'Read-only',
    empty: 'No items',
  },

  nav: {
    brand: 'Karexia',
    groups: {
      general: 'General',
      creation: 'Creation',
      prospection: 'Prospecting & CRM',
      engager: 'Engage',
      ai: 'AI',
      system: 'System',
    },
    items: {
      dashboard: 'Dashboard',
      content: 'Content',
      calendar: 'Calendar',
      publishing: 'Publishing',
      contentQueue: 'Content Queue',
      bulkSchedule: 'Bulk Scheduling',
      mediaStudio: 'Bulk Media Studio',
      platforms: 'Platforms',
      ads: 'Ads',
      mediaLibrary: 'Media library',
      analytics: 'Analytics',
      design: 'Design',
      workflows: 'Workflows',
      crm: 'CRM & Leads',
      discovery: 'Discovery (live)',
      prospectionOffers: 'Offers & ICP',
      prospectionRecommend: 'Recommendations',
      prospectionCalendar: 'Prospection calendar',
      accountIntelligence: 'Account intelligence',
      carouselEditor: 'Carousel editor',
      richContentStudio: 'Rich content studio',
      prospect: 'B2B database (directory)',
      marketplace: 'Marketplace',
      social: 'Social leads',
      outreach: 'Email outreach',
      inbox: 'Social inbox',
      community: 'Community',
      listening: 'Social listening',
      bio: 'Link-in-bio',
      projects: 'Projects',
      aiTeam: 'AI team',
      aiOffice: 'AI office',
      aiTeams: 'AI teams',
      aiDeliverables: 'AI deliverables',
      aiSchedules: 'AI schedules',
      aiControl: 'AI control',
      aiAuditTrail: 'AI audit trail',
      staffQuestions: 'Agent questions',
      grants: 'Grant requests',
      superadminFleet: 'Platform fleet',
      orchestration: 'AI orchestration',
      aiAssistant: 'AI assistant',
      contentStudio: 'AI content studio',
      ghostwriter: 'AI ghostwriter',
      repurpose: 'Repurposing',
      hookLibrary: 'Hook library',
      brandVoice: 'Brand voice',
      abCopy: 'A/B copy tests',
      suggestions: 'AI suggestions',
      personalization: 'Personalization',
      communityAi: 'Community AI',
      ragDocuments: 'RAG documents',
      memory: 'AI memory',
      approvals: 'Approvals',
      aiActivity: 'AI activity',
      programs: 'Partner programs',
      checklists: 'In-app checklists',
      settings: 'Settings',
    },
    header: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      toggleSidebar: 'Collapse or expand the sidebar',
      breadcrumbRoot: 'Karexia',
      breadcrumbAria: 'Breadcrumb',
      searchPlaceholder: 'Search…',
      openCommandPalette: 'Open the command palette',
      quickActions: 'Quick actions',
      create: 'Create',
      createItem: 'Create an item',
      shortcuts: 'Keyboard shortcuts',
      showShortcuts: 'Show keyboard shortcuts',
    },
    quickCreate: {
      newContent: 'New content',
      newContact: 'New contact',
      newCompany: 'New company',
      newCampaign: 'New campaign',
      newProject: 'New project',
      newDesign: 'New design',
    },
    quickAccess: {
      search: 'Search (⌘K)',
      newContent: 'New content',
      newContact: 'New contact',
      newDeal: 'New deal',
      inbox: 'Social inbox',
      assistant: 'AI assistant',
    },
    user: {
      fallbackName: 'User',
      connectedAccount: 'Connected account',
      settings: 'Settings',
      signout: 'Sign out',
    },
    footer: {
      environment: 'Runtime environment',
      whatsNew: "What's new",
      shortcuts: 'Shortcuts',
      privacy: 'Privacy',
      links: 'Footer links',
      statusTitle: 'Service status (/health polled every 30 s)',
      operational: 'Operational',
      unavailable: 'Service unavailable',
      checking: 'Checking…',
    },
    primaryNav: 'Primary navigation',
  },

  settings: {
    title: 'Settings',
    subtitle:
      'AI credits & billing, agent and model registry, team, profile and social connections.',
    navAria: 'Settings navigation',
    tabs: {
      credits: 'Credits & Billing',
      subscription: 'Subscription',
      referral: 'Referral',
      aiRegistry: 'AI registry',
      models: 'AI models',
      governance: 'AI governance',
      trustCenter: 'Trust Center',
      organization: 'Organization',
      notifications: 'Notifications',
      roles: 'Roles & permissions',
      connectors: 'Connectors',
      developers: 'Developer API',
      integrations: 'Integrations',
      connections: 'Connected accounts',
      team: 'Team',
      profile: 'Profile',
    },
  },

  profile: {
    tabsAria: 'Profile sections',
    tabProfile: 'Profile',
    tabConnections: 'Connections',
    emailVerified: 'Email verified',
    emailUnverified: 'Email not verified',
    noRole: 'no role',
    stats: {
      roles: 'Roles',
      session: 'Session',
      expiresAt: 'expires at',
    },
    account: {
      title: 'Account',
      name: 'Name',
      email: 'Email',
      username: 'Username',
      tenant: 'Tenant',
      userId: 'User ID',
      roles: 'Roles',
    },
    prefs: {
      title: 'Preferences',
      language: 'Language',
      languageHint: 'Preference remembered on this device.',
      theme: 'Theme',
      themeHint: '“System” follows the browser’s light/dark setting.',
      themeSystem: 'System',
      themeLight: 'Light',
      themeDark: 'Dark',
      appearance: 'Appearance',
      appearanceHint: 'Light/dark toggle and accent color — also available from the header.',
    },
  },

  platforms: {
    connections: {
      needsReconnectBadge: 'Reconnect required',
      needsReconnectHint:
        'The stored token can no longer be read on the server — this connection can no longer publish. Reconnect the account to fix it.',
      reconnectAction: 'Reconnect',
    },
  },

  integrationsStatus: {
    pageTitle: 'Integrations & vendor keys',
    pageSubtitle:
      'What is actually configured on the platform (Vault) versus what is missing — never a secret value, only status.',
    restrictedTitle: 'Restricted to the platform superadmin',
    restrictedBody:
      'This cross-service screen is reserved for platform operators. It is not tied to your organization’s owner/admin role.',
    refresh: 'Refresh',
    refreshing: 'Refreshing…',
    loading: 'Loading integrations…',
    errorGeneric: 'Could not load the integrations status.',
    configuredBadge: 'Configured',
    missingBadge: 'Missing',
    unknownBadge: 'Unknown',
    summaryConfigured: 'Configured',
    summaryMissing: 'Missing',
    summaryUnknown: 'Unknown',
    summaryTotal: 'Catalog keys',
    columnKey: 'Key',
    columnCategory: 'Category',
    columnOwner: 'Owning service',
    columnStatus: 'Status',
    columnNote: 'Note',
    emptyLabel: 'No integrations to display.',
    categoryLabels: {
      LLM: 'LLM',
      STT: 'Speech-to-text',
      TTS: 'Text-to-speech',
      image: 'Image',
      video: 'Video',
      enrichment: 'Enrichment',
      email: 'Email',
      payments: 'Payments',
      social: 'Social',
      geo: 'Geo',
    },
  },

  publishingWizard: {
    pageTitle: 'AI Assistant — post creation',
    pageSubtitle:
      'Generate a post step by step (theme → title → description → detailed text), with an AI review at every stage, then send it to the composer.',
    backToComposer: 'Back to composer',
    stages: {
      theme: {
        label: 'Theme',
        fieldLabel: 'Content theme',
        placeholder: 'E.g. product launch, HR tips, case study…',
        hint: 'The theme drives every following stage.',
      },
      title: {
        label: 'Title',
        fieldLabel: 'Title / hook',
        placeholder: 'A catchy title…',
        hint: 'Pick a candidate or edit the post title.',
      },
      description: {
        label: 'Description',
        fieldLabel: 'Description / summary',
        placeholder: 'A one- or two-sentence summary…',
        hint: 'A short summary expanded in the next stage.',
      },
      detail: {
        label: 'Detailed text',
        fieldLabel: 'Full post text',
        placeholder: 'The full text, ready to publish…',
        hint: 'The final text sent to the composer.',
      },
    },
    seedLabel: 'Keywords / context (optional)',
    seedHint: 'Helps theme generation — industry, audience, desired tone…',
    seedPlaceholder: 'E.g. industry, audience, desired tone…',
    platformLabel: 'Platform (context)',
    generate: 'Generate candidates',
    regenerate: 'Regenerate candidates',
    generating: 'Generating…',
    candidatesTitle: 'Candidates',
    noCandidatesError: 'No candidates returned — type your text directly below.',
    genericError: 'Generation failed — retry or type your text manually.',
    modelLabel: 'Model',
    reviewSectionTitle: '🧠 AI review of this stage',
    back: 'Previous stage',
    next: 'Next stage',
    sendToComposer: 'Send to composer',
    sentToComposer: 'Post sent to the composer.',
    emptyStageError: 'Fill in or generate text before continuing.',
    quickCreateCta: 'Generate with AI',
  },

  publishingSource: {
    label: 'Content source',
    aiGenerated: 'AI (staged assistant)',
    aiGeneratedHint: 'Open the AI assistant to generate the content step by step, then send it here.',
    aiOpenWizard: 'Open the AI assistant',
    manualExternal: 'Manual — external media',
    manualExternalHint:
      'Hand-written with externally-sourced media (upload, library, AI image or URL import).',
    manualDesign: 'Manual — graphic design',
    manualDesignHint: 'Hand-written with a visual created in the graphic designer.',
    designAttached: 'Designer visual attached to the post.',
  },

  publishingTracking: {
    pageTitle: 'Post tracking',
    pageSubtitle: 'Metrics history, trend and post-publication actions for every published post.',
    pickerLabel: 'Published posts',
    pickerEmpty: 'No published post yet.',
    historyTitle: 'Metrics history',
    historyEmpty: 'No metrics snapshot recorded for this post.',
    timeseriesTitle: 'Overall trend (last 30 days)',
    timeseriesEmpty: 'Not enough data to plot a trend.',
    trackingUnavailable: 'Tracking is not available on this environment yet.',
    refresh: 'Refresh metrics',
    refreshing: 'Refreshing…',
    refreshed: 'Metrics refreshed.',
    actionsTitle: 'Actions',
    actionBoost: 'Boost',
    actionReshare: 'Reshare',
    actionReply: 'Reply',
    actionRetry: 'Retry',
    actionReschedule: 'Reschedule',
    replyPlaceholder: 'Your reply…',
    replySend: 'Send reply',
    rescheduleLabel: 'New date and time',
    rescheduleConfirm: 'Reschedule',
    actionUnsupported: 'Action not supported for this platform or account.',
    actionSuccess: 'Action completed successfully.',
    actionError: 'Action failed — please retry.',
    openTracking: 'Tracking & actions',
    engagementTitle: 'Engagement',
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
    reach: 'Reach',
    impressions: 'Impressions',
    capturedAt: 'Captured on',
    noJobContext: 'Job details unknown (direct access) — some actions are hidden.',
  },
  dataLoop: dataLoopEn,
  autopilot: autopilotEn,
  communityAcquisition,
  crmEmailBulk,
  crmEmailDrafts,
  emailPurposeTemplates,
  /** Multi-account connectors (accounts list, plan limit, Stripe / GitHub selectors). */
  connectorAccounts,
  i5: i5En,

  /** R12 blog/CMS publishing (Platforms connectors + Long-Form « Publier sur le blog »),
   *  R13 swipe deck (/content/deck) and the daily-batch schedule template. */
  blogPublishing: {
    sectionTitle: 'Blogs & CMS',
    sectionSubtitle: 'Publish your long-form articles to your blog through the official APIs (WordPress, Webflow, Ghost, Dev.to, Hashnode). Credentials are verified live, then encrypted.',
    platformWordpress: 'WordPress',
    platformWebflow: 'Webflow',
    platformGhost: 'Ghost',
    platformDevto: 'Dev.to',
    platformHashnode: 'Hashnode',
    authWordpress: 'REST API + application password (Profile → Application Passwords).',
    authWebflow: 'Data API v2 — site API token (CMS read/write scopes) + collection ID.',
    authGhost: 'Admin API — custom integration key (Settings → Integrations).',
    authDevto: 'Forem API — DEV API key (Settings → Extensions).',
    authHashnode: 'GraphQL — personal access token (Settings → Developer).',
    connect: 'Connect',
    connectTitle: 'Connect {{platform}}',
    connecting: 'Verifying…',
    connectedCount: '{{count}} connected',
    connectedToast: '{{name}} connected — credentials verified.',
    docsLink: 'Official documentation',
    fieldSiteUrl: 'Site URL (https)',
    fieldUsername: 'WordPress username',
    fieldAppPassword: 'Application password',
    fieldApiToken: 'Site API token',
    fieldCollectionId: 'CMS collection ID',
    fieldPublicBaseUrl: 'Public collection URL (optional)',
    fieldApiUrl: 'Admin API URL (https)',
    fieldAdminApiKey: 'Admin API key (id:secret)',
    fieldApiKey: 'API key',
    fieldToken: 'Personal access token',
    fieldPublicationId: 'Publication ID (optional)',
    fieldDisplayName: 'Display name (optional)',
    hintPublicBaseUrl: 'E.g. https://www.mysite.com/blog — used to build the published article link.',
    hintPublicationId: 'Empty = your first Hashnode blog.',
    secretNote: 'The secret is verified with {{platform}}, then stored encrypted (AES-256); it is never shown again.',
    errInvalidCredentials: '{{platform}} rejected the credentials: {{message}}',
    errUnreachable: 'Site unreachable: {{message}}',
    errConfig: 'Invalid configuration: {{message}}',
    errGeneric: 'Blog operation failed. Please retry.',
    connectedListTitle: 'Connected blogs',
    noBlogs: 'No blog connected.',
    statusActive: 'Active',
    statusExpired: 'Invalid credentials',
    verify: 'Verify',
    verifyOk: '{{name}}: credentials valid.',
    verifyKo: '{{name}}: credentials rejected — reconnect the blog.',
    disconnect: 'Disconnect',
    disconnectConfirm: 'Disconnect “{{name}}”? The stored credentials will be deleted.',
    disconnected: '{{name}} disconnected.',
    panelTitle: 'Publish to blog',
    notConnected: 'No blog connected — connect WordPress, Webflow, Ghost, Dev.to or Hashnode in Platforms to publish this article.',
    connectCta: 'Connect a blog',
    targetsLabel: 'Target blogs',
    titleLabel: 'Article title',
    statusLabel: 'Status',
    statusDraft: 'Draft',
    statusDraftHint: 'Created as a draft on the blog — nothing is publicly visible.',
    statusPublish: 'Publish',
    statusPublishHint: 'The article goes live immediately.',
    canonicalLabel: 'Canonical URL (optional)',
    canonicalHint: 'For an article already published elsewhere: the platform references it as the original (SEO).',
    canonicalWordpressNote: 'WordPress exposes no canonical field over its API: an “Originally published at…” link is added at the top.',
    tagsLabel: 'Tags',
    tagsHint: 'Comma-separated. Dev.to: 4 max, Hashnode: 5 max.',
    excerptLabel: 'Excerpt / summary (optional)',
    submitDraft: 'Save as draft',
    submitPublish: 'Publish now',
    publishing: 'Sending…',
    confirmPublishTitle: 'Publish publicly?',
    confirmPublishMessage: 'The article will go live publicly on {{count}} blog(s). Continue?',
    selectTarget: 'Select at least one blog.',
    resultPublished: 'Published',
    resultDraftSaved: 'Draft created',
    resultFailed: 'Failed',
    resultPending: 'Awaiting approval',
    resultNoLink: '(no public URL for a draft)',
    openPost: 'Open',
    doneToast: '{{ok}} succeeded, {{failed}} failed.',
    historyTitle: 'Blog publishing history',
    historyEmpty: 'No blog publication yet.',
    historyRefresh: 'Refresh',
    importOwn: 'Paste an existing article (Markdown)',
    importOwnHint: 'Write or paste your own article in the editor below, without AI generation (no credits).',
  },
  swipeDeck: {
    pageTitle: 'Approval deck',
    pageSubtitle: 'Review the nightly batch drafts with a gesture: → approve, ← reject, ↓ skip. Nothing is published automatically.',
    dayLabel: 'Batch of',
    counterTotal: 'Total',
    counterApproved: 'Approved',
    counterRejected: 'Rejected',
    counterSkipped: 'Skipped',
    counterRemaining: 'Remaining',
    counterScheduled: 'Scheduled',
    emptyTitle: 'No batch to review',
    emptyHint: 'Create a “daily batch” in AI schedules: every night, N drafts land here.',
    emptyCta: 'Create a daily batch',
    doneTitle: 'Batch fully reviewed',
    doneHint: 'Schedule the approved drafts below.',
    cardOf: 'Card {{index}} / {{total}}',
    approve: 'Approve',
    reject: 'Reject',
    skip: 'Skip',
    edit: 'Edit',
    undo: 'Undo last action',
    shortcuts: 'Shortcuts: → or A approve · ← or R reject · ↓ or S skip · E edit · U undo. On mobile: swipe the card.',
    readOnly: 'Read-only: only reviewers (owner, admin, manager, publisher) can approve.',
    toastApproved: 'Approved — ready to schedule.',
    toastStageApproved: 'Stage approved — awaiting the next stage.',
    toastRejected: 'Rejected — sent back to draft.',
    toastSkipped: 'Skipped — stays in the batch.',
    toastUndone: 'Action undone.',
    toastError: 'Action failed: {{message}}',
    nothingToUndo: 'Nothing to undo.',
    editTitle: 'Edit draft',
    editTitleField: 'Internal title',
    editBodyField: 'Post text',
    editSave: 'Save',
    editSaved: 'Draft updated.',
    approvedTitle: 'Approved — to schedule',
    approvedEmpty: 'No approved draft for this batch.',
    scheduleBest: 'Schedule at best time',
    scheduleAll: 'Schedule all approved',
    scheduledAt: 'Scheduled for {{when}}',
    scheduledToast: 'Scheduled for {{when}} (best time).',
    noSlot: 'No available slot found — schedule from the calendar.',
    skippedTitle: 'Skipped',
    rejectedTitle: 'Rejected',
    restore: 'Back to the batch',
    loading: 'Loading batch…',
    loadError: 'Could not load the deck. Please retry.',
  },
  dailyBatch: {
    sectionTitle: 'Daily content batch',
    sectionSubtitle: 'Every night the AI prepares N drafts in the review queue (approval deck). Credits are checked before generating; nothing is published without your approval.',
    newBatch: 'New daily batch',
    openDeck: 'Open the deck',
    formTitle: 'New daily batch',
    fieldName: 'Batch name',
    fieldItems: 'Drafts per night',
    fieldItemsHint: 'Your plan cap: {{limit}} per night.',
    fieldPlatforms: 'Target networks',
    fieldThemes: 'Themes / pillars (one per line)',
    fieldTone: 'Tone',
    fieldAudience: 'Audience',
    fieldLanguage: 'Language',
    fieldTimezone: 'Time zone',
    fieldHour: 'Generation hour (local)',
    fieldActive: 'Enable nightly generation',
    fieldActiveHint: 'Disabled = the batch only runs via “Run now”.',
    estimate: 'Estimate: {{credits}} credits per batch ({{perItem}} per draft) — available balance: {{available}}.',
    balanceUnknown: 'unknown',
    planNotIncluded: 'Your plan does not include AI automations — the daily batch is unavailable.',
    create: 'Create batch',
    creating: 'Creating…',
    created: 'Daily batch created.',
    cancel: 'Cancel',
    summary: '{{count}} draft(s)/night · {{platforms}} · {{hour}}:00 ({{tz}})',
    paused: 'Paused',
    active: 'Active',
    pause: 'Pause',
    resume: 'Enable',
    runNow: 'Run now',
    running: 'Generating…',
    delete: 'Delete',
    deleteConfirm: 'Delete the batch “{{name}}”? Drafts already created stay in the review queue.',
    runDone: '{{count}} draft(s) added to the deck.',
    historyTitle: 'Recent batches',
    historyEmpty: 'No batch run yet.',
    runRow: '{{date}} · {{created}}/{{effective}} draft(s) · {{trigger}}',
    triggerScheduled: 'nightly',
    triggerManual: 'manual',
    statusCompleted: 'Completed',
    statusInsufficientCredits: 'Insufficient credits',
    statusPlanBlocked: 'Not in plan',
    statusLlmUnavailable: 'AI model unavailable',
    statusIntakeFailed: 'Queue intake failed',
    statusFailed: 'Failed',
    statusSkipped: 'Already ran today',
    errorGeneric: 'Batch operation failed. Please retry.',
  },

  programs: {
    title: 'Partner programs',
    subtitle: 'Referral and affiliate programs for your own product: tracked links, attribution, commissions and payouts.',
    newProgram: 'New program',
    empty: 'No program yet',
    emptyHint: 'Create a program, add partners and share their tracked links.',
    loadError: 'Could not load programs.',
    back: '← Programs',
    open: 'Open',
    colName: 'Program',
    colStatus: 'Status',
    colPartners: 'Partners',
    colClicks: 'Clicks',
    colSignups: 'Signups',
    colConversions: 'Conversions',
    statusActive: 'Active',
    statusPaused: 'Paused',
    statusArchived: 'Archived',
    pause: 'Pause',
    resume: 'Resume',
    deleteProgram: 'Delete program',
    deleteConfirm: 'Delete “{{name}}” and all its data (partners, clicks, commissions)?',
    deleted: 'Program deleted.',
    caslNote: 'Partners share their own link. E-mail invitations are commercial electronic messages: they only go out with valid consent (CASL / Law 25).',
    form: {
      name: 'Program name',
      slug: 'Identifier (slug)',
      slugHint: '3–40 characters: a-z, 0-9, dash. Derived from the name when empty.',
      kind: 'Type',
      kindAffiliate: 'Affiliate (commission on sales)',
      kindReferral: 'Referral (customers who recommend)',
      destination: 'Destination URL',
      destinationHint: 'Your product signup page (https). The tracked link appends ?kx_ref=…',
      rewardType: 'Reward',
      rewardPercent: 'Percentage of each payment',
      rewardFixed: 'Fixed amount per converted customer',
      rate: 'Commission (%)',
      fixedAmount: 'Fixed amount',
      currency: 'Currency',
      months: 'Commission duration (months of payments)',
      cookieDays: 'Attribution window (days)',
      holdDays: 'Hold period before payout (days)',
      minPayout: 'Minimum payout',
      maxSignupsDay: 'Max signups per partner / 24 h (anti-fraud)',
      payoutMethod: 'Payout method',
      payoutManual: 'CSV export (manual payout)',
      payoutStripe: 'Stripe Connect (your Stripe account)',
      description: 'Description',
      terms: 'Program terms',
      create: 'Create program',
      save: 'Save',
      created: 'Program created.',
      saved: 'Program saved.',
    },
    secret: {
      title: 'Server secret key',
      notice: 'Shown once: keep it in your backend to report payments (X-Program-Secret header).',
      copy: 'Copy',
      copied: 'Copied.',
      rotate: 'Regenerate secret key',
      rotated: 'New secret key generated.',
      publishableKey: 'Publishable key (browser)',
      secretHint: 'Secret key',
      close: 'I saved the key',
    },
    tabs: {
      overview: 'Overview',
      partners: 'Partners',
      referrals: 'Signups',
      commissions: 'Commissions',
      payouts: 'Payouts',
      integration: 'Integration',
      settings: 'Settings',
    },
    totals: {
      clicks: 'Clicks',
      uniqueClicks: 'Unique clicks',
      signups: 'Signups',
      held: 'Under review',
      rejected: 'Rejected',
      conversions: 'Conversions',
      revenue: 'Attributed revenue',
      pending: 'Commissions on hold',
      approved: 'Payable commissions',
      paid: 'Paid commissions',
    },
    partners: {
      add: 'Add a partner',
      name: 'Name',
      email: 'E-mail',
      code: 'Link code',
      codeHint: 'Optional — generated otherwise. Link: /r/{tenant}/{code}',
      rateOverride: 'Partner-specific commission (%)',
      link: 'Tracked link',
      copyLink: 'Copy link',
      portalLink: 'Portal access link',
      portalLinkCreated: 'Access link created (single use) — share it with the partner.',
      invite: 'Invite by e-mail',
      inviteSent: 'Invitation sent.',
      inviteBlocked: 'Invitation blocked: {{reason}}',
      inviteStatus: 'Invitation',
      suspend: 'Suspend',
      activate: 'Reactivate',
      remove: 'Remove',
      removeConfirm: 'Remove partner “{{name}}”?',
      added: 'Partner added.',
      empty: 'No partner.',
      statusInvited: 'Invited',
      statusActive: 'Active',
      statusSuspended: 'Suspended',
    },
    referrals: {
      customer: 'Customer',
      status: 'Status',
      fraud: 'Reason',
      source: 'Source',
      created: 'Date',
      approve: 'Approve',
      reject: 'Reject',
      empty: 'No attributed signup.',
      statusSignedUp: 'Signed up',
      statusHeld: 'Under review',
      statusRejected: 'Rejected',
      statusConverted: 'Converted',
    },
    commissions: {
      amount: 'Commission',
      base: 'Payment',
      status: 'Status',
      holdUntil: 'Payable on',
      void: 'Void',
      voidConfirm: 'Void this commission (refund / fraud)?',
      voided: 'Commission voided.',
      empty: 'No commission.',
      recordTitle: 'Report a payment',
      customerRef: 'Customer identifier',
      amount_input: 'Amount paid (units, e.g. 49.00)',
      sourceRef: 'Payment reference',
      record: 'Report',
      recorded: 'Payment reported.',
      skipped: 'No commission: {{reason}}',
      statusPending: 'On hold',
      statusApproved: 'Payable',
      statusPaid: 'Paid',
      statusVoid: 'Voided',
    },
    payouts: {
      exportCsv: 'Export payouts (CSV)',
      exported: '{{count}} payout(s) exported.',
      nothing: 'No payable commission above the minimum.',
      stripePay: 'Pay with Stripe Connect',
      stripeDone: 'Stripe payouts processed.',
      stripeNotConfigured: 'Stripe Connect is not configured: add your Stripe key in Settings or use the CSV export.',
      markPaid: 'Mark paid',
      externalRef: 'Transfer reference',
      paidOk: 'Payout marked as paid.',
      cancel: 'Cancel export',
      empty: 'No payout.',
      method: 'Method',
      status: 'Status',
      amount: 'Amount',
      partner: 'Partner',
    },
    integration: {
      title: 'Add tracking to your product',
      linkFormat: 'Partner link format',
      signupTitle: '1. Report the signup (browser)',
      signupHint: 'On your signup page, keep the kx_ref URL parameter, then once the account exists:',
      conversionTitle: '2. Report payments (server)',
      conversionHint: 'From your backend or billing webhook, with the secret key:',
      eventsTitle: 'Or through product events',
      eventsHint: 'A “signup” event carrying kx_ref, then “paid” with amount_cents, are attributed automatically.',
    },
    stripe: {
      title: 'Stripe Connect payouts',
      configured: 'Configured — account {{account}} ({{mode}})',
      notConfigured: 'Not configured. Payouts go through the CSV export.',
      keyLabel: 'Stripe secret key (sk_… or rk_…)',
      keyHint: 'Your own Stripe account with Connect enabled (Stripe approval required). The key is verified then encrypted; it is never shown again.',
      save: 'Verify and save',
      saved: 'Stripe key verified.',
      remove: 'Remove key',
      removed: 'Stripe key removed.',
      live: 'live',
      test: 'test',
    },
  },

  partnerPortal: {
    title: 'Partner portal',
    loading: 'Opening your portal…',
    invalid: 'This link is no longer valid',
    invalidHint: 'Access links are single use and expire. Request a new link below.',
    requestTitle: 'Get a new link',
    tenantLabel: 'Company identifier',
    emailLabel: 'Your e-mail',
    send: 'Send the link',
    sent: 'If this e-mail is registered, a link was just sent.',
    yourLink: 'Your tracked link',
    copy: 'Copy',
    copied: 'Link copied.',
    rule: 'Reward',
    rulePercent: '{{rate}}% of each payment for {{months}} months',
    ruleFixed: '{{amount}} per converted customer',
    cookie: 'Attribution for {{days}} days after the click',
    clicks: 'Clicks',
    signups: 'Signups',
    conversions: 'Conversions',
    pending: 'On hold',
    approved: 'Payable',
    paid: 'Paid',
    commissions: 'Commissions',
    payouts: 'Payouts received',
    noCommissions: 'No commission yet.',
    noPayouts: 'No payout yet.',
    connect: 'Set up my payouts (Stripe)',
    connectUnavailable: 'This program’s payouts are made manually by the company.',
    terms: 'Program terms',
    signOut: 'Sign out',
  },

  checklists: {
    title: 'In-app checklists',
    subtitle: 'Guide your users inside your product: each step ticks when its event is tracked.',
    new: 'New checklist',
    empty: 'No checklist',
    emptyHint: 'Create an onboarding checklist, then embed the widget in your app.',
    loadError: 'Could not load checklists.',
    back: '← Checklists',
    name: 'Internal name',
    kind: 'Format',
    kindChecklist: 'Onboarding checklist',
    kindAnnouncement: 'Announcement',
    titleLabel: 'Displayed title',
    description: 'Displayed description',
    steps: 'Steps',
    addStep: 'Add a step',
    stepId: 'ID',
    stepTitle: 'Title',
    stepEvent: 'Event that ticks the step',
    stepDescription: 'Description',
    ctaLabel: 'Button',
    ctaUrl: 'Button link',
    removeStep: 'Remove',
    origins: 'Allowed origins',
    originsHint: 'One per line (e.g. https://app.myproduct.com). Empty = any.',
    color: 'Color',
    position: 'Position',
    positionBottomRight: 'Bottom right',
    positionBottomLeft: 'Bottom left',
    positionInline: 'Inline (#karexia-checklist)',
    locale: 'Widget language',
    verifyIdentity: 'Identity verification (recommended)',
    verifyIdentityHint: 'Your server signs the user id (HMAC). Only verified events trigger lifecycle e-mails.',
    statusDraft: 'Draft',
    statusLive: 'Live',
    statusArchived: 'Archived',
    publish: 'Go live',
    unpublish: 'Back to draft',
    save: 'Save',
    saved: 'Checklist saved.',
    created: 'Checklist created.',
    create: 'Create',
    delete: 'Delete',
    deleteConfirm: 'Delete “{{name}}” and its users’ progress?',
    deleted: 'Checklist deleted.',
    preview: 'Preview',
    embed: 'Embed',
    embedHint: 'Paste this code in your app (replace the user id and the server-side signature).',
    copy: 'Copy',
    copied: 'Copied.',
    identitySecret: 'Identity verification secret',
    reveal: 'Reveal secret',
    rotate: 'Regenerate',
    rotated: 'Secret regenerated — update your server.',
    identityUnavailable: 'Identity verification is unavailable on this environment.',
    testTitle: 'Test with a user',
    testUser: 'Test user id',
    testEvent: 'Event to simulate',
    simulate: 'Simulate the event',
    simulated: 'Event recorded.',
    liveOnly: 'Put the checklist live to test it.',
    progress: 'User progress',
    users: 'Users',
    completed: 'Completed',
    dismissed: 'Dismissed',
    perStep: 'Per step',
    resetUser: 'Reset',
    noProgress: 'No user yet.',
    verified: 'Verified',
    unverified: 'Unverified',
    done: 'done',
  },

  lifecycle: {
    group: 'Lifecycle',
    nodeProductEvent: 'Product event',
    nodeLifecycleEmail: 'Lifecycle e-mail',
    descProductEvent: 'Starts the flow when your product sends an event (signup, activated, inactive_7d, trial_ending…).',
    descLifecycleEmail: 'E-mails the user — classified CEM or transactional, consent checked (CASL / Law 25).',
    event: 'Event',
    customEvent: 'Other event…',
    customEventName: 'Event name',
    conditions: 'Property conditions',
    addCondition: 'Add a condition',
    property: 'Property',
    op: 'Operator',
    value: 'Value',
    match: 'Match',
    matchAll: 'All',
    matchAny: 'Any',
    allowReentry: 'Re-enrol on every event (otherwise once per user)',
    to: 'Recipient',
    toHint: 'Default: the event’s “email” property.',
    subject: 'Subject',
    html: 'Body (HTML)',
    templateHint: 'Variables: {inputs[properties][first_name]}, {inputs[subject_id]}…',
    classification: 'CASL classification',
    transactional: 'Transactional / relationship (not a CEM)',
    cem: 'Commercial electronic message (CEM) — consent required',
    purpose: 'Purpose',
    purposeOnboarding: 'Onboarding (product instructions)',
    purposeAccountNotice: 'Account / subscription notice',
    purposeTransaction: 'Transaction confirmation',
    purposeMarketing: 'Marketing / promotion',
    purposeReengagement: 'Re-engagement',
    purposeNewsletter: 'Newsletter',
    mode: 'Mode',
    modeSend: 'Send',
    modeDryRun: 'Test (verdict only, nothing is sent)',
    complianceNote: 'A transactional message is only accepted for a transactional purpose; inactive_7d is always a CEM. Without consent a CEM is blocked and the block shows in the run log.',
    runsTitle: 'Run log',
    runsEmpty: 'No run yet.',
    refresh: 'Refresh',
    advanceClock: 'Advance the clock (test)',
    advanced: 'Run resumed.',
    enrollments: 'Enrolled users',
    status: 'Status',
    reason: 'Reason',
    stepOutput: 'Result',
  },

  aso: {
    title: 'ASO audit (App Store / Google Play)',
    subtitle: 'Score your app listing, spot missing keywords and compare against competitors.',
    platformIos: 'iOS — App Store',
    platformAndroid: 'Android — Google Play',
    iosSourceNote: 'The iOS listing is read through Apple’s official iTunes Lookup API. The subtitle and the private keyword field are not exposed: paste your subtitle to audit it.',
    androidSourceNote: 'Google publishes no official API to read public Play Store listings and scraping is not allowed: paste your listing text (title, descriptions) to audit it.',
    appRef: 'App Store link, app id (id123…) or bundle id',
    appRefPlaceholder: 'https://apps.apple.com/ca/app/…/id123456789',
    country: 'Store country',
    subtitleField: 'iOS subtitle (optional)',
    competitorRefs: 'iOS competitors (links or ids, one per line, 5 max)',
    keywords: 'Target keywords (comma-separated)',
    keywordsPlaceholder: 'family budget, expense tracker',
    packageName: 'Package name (optional)',
    androidTitle: 'Listing title',
    shortDescription: 'Short description (80 chars)',
    fullDescription: 'Full description',
    screenshots: 'Number of screenshots',
    hasVideo: 'The listing has a promo video',
    rating: 'Average rating (0–5)',
    ratingsCount: 'Number of ratings',
    lastUpdated: 'Last updated',
    androidCompetitors: 'Android competitors (pasted text)',
    addCompetitor: 'Add a competitor',
    competitorTitle: 'Competitor title',
    run: 'Run audit',
    running: 'Auditing…',
    score: 'ASO score',
    checks: 'Checks',
    recommendations: 'Recommendations',
    noRecommendations: 'No recommendations: the listing passes every check.',
    keywordCoverage: 'Keyword coverage',
    suggestedTerms: 'Frequent description terms missing from title/subtitle',
    comparison: 'Competitor comparison',
    competitorTermsMissing: 'Terms competitors use that your listing lacks',
    gaps: 'Gaps',
    history: 'Audit history',
    historyEmpty: 'No audits yet.',
    open: 'Open',
    delete: 'Delete',
    deleteConfirm: 'Delete this audit from the history?',
    warnings: 'Warnings',
    severityHigh: 'High priority',
    severityMedium: 'Medium',
    severityLow: 'Low',
    colApp: 'App',
    colScreens: 'Screens',
    colRating: 'Rating',
    colRatings: 'Ratings',
    colDesc: 'Desc. (chars)',
    colUpdated: 'Updated (days)',
    you: 'You',
    area_title: 'Title',
    area_subtitle: 'Subtitle / short description',
    area_description: 'Description',
    area_keywords: 'Keywords',
    area_visuals: 'Visuals',
    area_ratings: 'Ratings',
    area_freshness: 'Freshness',
    area_localization: 'Localization',
    required: 'Required field',
  },

  prMedia: {
    title: 'Media database / press relations',
    subtitle: 'Find relevant journalists and outlets for your announcements.',
    blockerTitle: 'Blocked on a vendor licence (external blocker)',
    blockerBody: 'A media-contacts database can only be provided legally through a licensed vendor (contract + official API). Karexia never collects journalist contact details by scraping. This feature will be wired as soon as a vendor contract and its API key are in place.',
    needed: 'What it takes to turn it on',
    need1: 'A contract with a licensed media-database vendor allowing use inside a third-party app.',
    need2: 'The vendor’s official API key, stored in the secrets vault (never in code).',
    need3: 'A CASL/Law 25 lawful basis for each outreach (press relations = individual, relevant, non-bulk messages).',
    meanwhile: 'Meanwhile you can:',
    alt1: 'Manage your own press contacts (obtained with consent) in the CRM with a “press” tag.',
    alt2: 'Draft your press releases in the Content Studio.',
    openCrm: 'Open CRM',
    openStudio: 'Open Studio',
  },

  support: {
    button: 'Support',
    buttonAria: 'Open support ({{count}} unread)',
    panelTitle: 'Karexia support',
    panelSubtitle: 'Write to the team: the answer arrives here, in the app.',
    newRequest: 'New request',
    myRequests: 'My requests',
    noRequests: 'No requests yet.',
    subject: 'Subject',
    category: 'Category',
    message: 'Message',
    messagePlaceholder: 'Describe your question or the problem you hit…',
    attachments: 'Attachments (optional — 3 files, 2 MB max: image, PDF, text)',
    send: 'Send',
    sending: 'Sending…',
    reply: 'Reply',
    replyPlaceholder: 'Your message…',
    resolve: 'Mark as resolved',
    back: '← My requests',
    sent: 'Request sent — the team will answer here.',
    you: 'You',
    team: 'Karexia team',
    system: 'System',
    unread: '{{count}} unread',
    status_open: 'Open',
    status_pending: 'Waiting on you',
    status_resolved: 'Resolved',
    status_closed: 'Closed',
    cat_question: 'Question',
    cat_bug: 'Problem / bug',
    cat_billing: 'Billing',
    cat_feature: 'Suggestion',
    cat_account: 'Account',
    cat_other: 'Other',
    errFileType: 'File type not allowed: {{name}}',
    errFileSize: 'File too large (2 MB max): {{name}}',
    errFileCount: '3 files maximum.',
    closedNote: 'This request is closed — open a new one if needed.',
    guides: 'Daily Moves how-to guides',
    docs: 'Documentation',
    page: 'Origin page',
    delete: 'Delete conversation',
    deleteConfirm: 'Permanently delete this conversation and its attachments?',
  },

  chatApps: {
    title: 'Chat apps (Slack, SMS)',
    subtitle: 'Talk to the Copilot from Slack or by text. Proposed actions are always confirmed here.',
    slackTitle: 'Slack — /karexia command and mentions',
    slackIntro: 'Link your Slack account to use /karexia <question> and @Karexia in your channels.',
    slackNotConfigured: 'Karexia’s Slack app is not configured yet by the platform administrator (Slack signing secret missing). Linking still works; commands start working once it is configured.',
    slackWorkspaceMissing: 'Your Slack workspace is not connected: connect “Slack” in Settings → Integrations to receive replies to mentions.',
    generateCode: 'Generate a link code',
    codeHelp: 'In Slack, type this command (valid 10 minutes, single use):',
    copy: 'Copy',
    copied: 'Copied',
    linkedAccounts: 'Linked Slack accounts',
    noSlackLinks: 'No Slack account linked.',
    unlink: 'Unlink',
    turns: '{{count}} exchange(s)',
    smsTitle: 'SMS — Copilot by text',
    smsIntro: 'Link your own number: you will get a confirmation text, reply YES to turn it on (express consent). Reply STOP anytime to stop, HELP for help.',
    smsNotConfigured: 'The SMS provider (Twilio) is not configured yet by the platform administrator: text linking becomes available once it is configured.',
    phone: 'Your mobile number',
    language: 'Text language',
    consent: 'I agree to receive texts from the Karexia Copilot on this number (express consent, revocable with STOP).',
    linkPhone: 'Send the confirmation text',
    smsPending: 'Waiting for your YES reply to the confirmation text.',
    smsActive: 'Active — text your question to {{number}}.',
    smsStatus: 'Status',
    pendingTitle: 'Actions proposed from Slack / SMS',
    pendingIntro: 'The Copilot never executes a write from a chat app: confirm or reject here.',
    noPending: 'No pending actions.',
    confirm: 'Confirm',
    reject: 'Reject',
    confirmed: 'Action confirmed.',
    rejected: 'Action rejected.',
    channel_slack: 'Slack',
    channel_sms: 'SMS',
    planGate: 'Copilot in Slack/SMS is included from the Starter plan.',
  },
  /** Copy for every page under src/routes/** (one module per route directory). */
  routes: routesEn,
  /** Copy for the app shell and shared modules (src/app, components, features, lib, stores, ui, auth). */
  shell: shellEn,
};

export default en;
